import type { ScreenBase } from "@frui.ts/screens";
import { ConductorOneChildActive, Router } from "@frui.ts/screens";
import { computed } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import { bound } from "@frui.ts/helpers";
import PayGateToTransferRefundsViewModel from "./payGateToTransferRefunds/payGateToTransferRefundsViewModel";
import DomesticRefundsViewModel from "./transferRefunds/domesticRefunds/domesticRefundsViewModel";
import ForeignRefundsViewModel from "./transferRefunds/foreignRefunds/foreignRefundsViewModel";
import SEPARefundsViewModel from "./transferRefunds/SEPARefunds/SEPARefundsViewModel";
import RefundRepository from "repositories/refundRepository";
import NotificationService, { NotificationType } from "services/notificationService";
import EventBus, {
  allRefundApprovalsApproved,
  approvedRefundsExported,
  blacklistedAccountsChanged,
  transferRefundPaymentTypeChanged,
} from "services/eventBus";
import ConfirmationService from "services/confirmationService";
import SecurityService from "services/securityService";
import type RefundType from "entities/refundType";
import PayGateRefundsViewModel from "./payGateRefunds/payGateRefundsViewModel";
import AppRouter from "services/router";
import SEPARefundDetailViewModel from "./transferRefunds/SEPARefunds/SEPARefundDetailViewModel";
import ForeignRefundDetailViewModel from "./transferRefunds/foreignRefunds/foreignRefundDetailViewModel";
import DomesticRefundDetailViewModel from "./transferRefunds/domesticRefunds/domesticRefundDetailViewModel";
import DistributorRefundsViewModel from "./distributorRefunds/distributorRefundsViewModel";
import type RefundApprovalsListViewModel from "./refundApprovalsListViewModel";
import TransferPaymentType from "entities/transferPaymentType";
import ItemApprovalMethod from "entities/itemApprovalMethod";

const navigationName = "refund-approvals";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [
    DomesticRefundsViewModel,
    ForeignRefundsViewModel,
    SEPARefundsViewModel,
    PayGateRefundsViewModel,
    PayGateToTransferRefundsViewModel,
    DistributorRefundsViewModel,
  ],
})
export default class RefundApprovalsViewModel extends ConductorOneChildActive<
  RefundApprovalsListViewModel<any, any, any> | DistributorRefundsViewModel
> {
  private unsubscriptions: Array<() => void> = [];

  constructor(
    private repository: RefundRepository,
    private localizationService: LocalizationService,
    private domesticRefundsViewModel: DomesticRefundsViewModel,
    private foreignRefundsViewModel: ForeignRefundsViewModel,
    private sepaRefundsViewModel: SEPARefundsViewModel,
    private payGateViewModel: PayGateRefundsViewModel,
    private payGateToTransferRefundsViewModel: PayGateToTransferRefundsViewModel,
    private distributorRefundsViewModel: DistributorRefundsViewModel,
    private appRouter: AppRouter,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService
  ) {
    super();
    this.navigationName = navigationName;
    this.children.push(
      this.domesticRefundsViewModel,
      this.foreignRefundsViewModel,
      this.sepaRefundsViewModel,
      this.payGateViewModel,
      this.payGateToTransferRefundsViewModel
    );

    if (this.securityService.allowTo("finance")) {
      this.children.push(this.distributorRefundsViewModel);
    }
  }

  protected onInitialize(): Promise<any> | void {
    if (!this.activeChild) {
      this.tryActivateChild(this.children[0]);
    }
    this.unsubscriptions.push(
      this.eventBus.subscribe(transferRefundPaymentTypeChanged, this.onTransferRefundPaymentTypeChanged),
      this.eventBus.subscribe(blacklistedAccountsChanged, this.onBankAccountBlacklistsChanged)
    );
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  closeChild(child: ScreenBase) {
    return false;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get canApproveAll() {
    return this.canProvideRefundApprovalAction;
  }

  @computed
  get canExportApproved() {
    return this.canProvideRefundApprovalAction && !this.noItemForExport;
  }

  @computed
  get noItemForExport() {
    return (
      this.domesticRefundsViewModel.noApprovalsForExport &&
      this.foreignRefundsViewModel.noApprovalsForExport &&
      this.sepaRefundsViewModel.noApprovalsForExport &&
      this.payGateViewModel.noApprovalsForExport &&
      this.payGateToTransferRefundsViewModel.noApprovalsForExport &&
      this.distributorRefundsViewModel.noApprovalsForExport
    );
  }

  @computed
  private get canProvideRefundApprovalAction() {
    return (
      this.securityService.allowTo("finance", "write") &&
      (this.securityService.allowTo("finance.factualauditing", "write") ||
        this.securityService.allowTo("finance.commandoperating", "write") ||
        this.securityService.allowTo("finance.chiefaccounting", "write"))
    );
  }

  @computed
  get canActionExportApprove() {
    return this.securityService.allowTo("finance.chiefaccounting", "write");
  }

  @bound
  async exportApprovedClick() {
    if (!this.canActionExportApprove) {
      return;
    }

    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmExportApprovedRefunds.title"), {
      confirmButton: { text: this.translate("confirmExportApprovedRefunds.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.exportApprovedRefunds();
        this.notificationService.add({ title: this.translate("refundsExported"), type: NotificationType.SUCCESS });
        this.eventBus.publish(allRefundApprovalsApproved());
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @computed
  get approveAllButtonTitle() {
    const activeChild = this.activeChild;

    if (!activeChild) {
      return this.translate("approveAll").replace("%name%", "");
    }

    return this.translate("approveAll").replace("%name%", `${this.translateRefundTypeEnum(activeChild.refundType)}`);
  }

  @bound
  async approveAllClick() {
    const activeChild = this.activeChild;

    if (!activeChild) {
      return;
    }

    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmAllRefundsApproval.title"), {
      confirmButton: { text: this.translate("confirmAllRefundsApproval.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.approveRefunds({
          refundType: activeChild.refundType,
          approvalMethod: ItemApprovalMethod.ApproveAll,
        });
        this.notificationService.add({ title: this.translate("allApproved"), type: NotificationType.SUCCESS });
        this.eventBus.publish(approvedRefundsExported());
        this.activeChild?.self.selectedItems.clear();
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @bound
  async approveSelectedClick() {
    const activeChild = this.activeChild;

    if (!activeChild) {
      return;
    }

    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmSelectedRefundsApproval.title"), {
      confirmButton: { text: this.translate("confirmSelectedRefundsApproval.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.approveRefunds({
          refundType: activeChild.refundType,
          approvalMethod: ItemApprovalMethod.ApproveSelected,
          refundIds: Array.from(activeChild.self.selectedItems as Set<{ id: string }>).map(item => item.id),
        });
        this.notificationService.add({ title: this.translate("allApproved"), type: NotificationType.SUCCESS });
        this.eventBus.publish(approvedRefundsExported());
        this.activeChild?.self.selectedItems.clear();
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @bound
  private async onTransferRefundPaymentTypeChanged(data: ReturnType<typeof transferRefundPaymentTypeChanged>) {
    const { refundId, newTransferType } = data.payload;

    switch (newTransferType) {
      case TransferPaymentType.Domestic:
        await this.appRouter.navigate(DomesticRefundDetailViewModel, { refundId });
        break;

      case TransferPaymentType.Sepa:
        await this.appRouter.navigate(SEPARefundDetailViewModel, { refundId });
        break;

      case TransferPaymentType.Foreign:
        await this.appRouter.navigate(ForeignRefundDetailViewModel, { refundId });
        break;
    }
  }

  @bound
  private onBankAccountBlacklistsChanged(data: ReturnType<typeof blacklistedAccountsChanged>) {
    this.children.forEach(children => {
      // Reload all non empty children lists - we have no data from BE to recognize by account numbers
      if (
        (children instanceof DomesticRefundsViewModel ||
          children instanceof ForeignRefundsViewModel ||
          children instanceof SEPARefundsViewModel ||
          children instanceof PayGateToTransferRefundsViewModel) &&
        children.isInitialized &&
        children.items.length
      ) {
        children.loadData();
      }
    });
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.${code}`, size);
  }

  translateRefundTypeEnum(value: RefundType) {
    return this.localizationService.translateEnum("refundType", value);
  }
}
